import { FC, Suspense, lazy, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FORMAT_FILTER_IDS, SEARCH_KEYS } from '../../../common/base-search-result/base-search-result.constants';
import CountrySpecific from '../../../common/country-specific/country-specific';
import CustomLink from '../../../common/custom-link';
import FreshUserCredits from '../../../common/fresh-user-credits/fresh-user-credits';
import LiveSessionBar from '../../../common/live-session-bar/live-session-bar';
import Logo from '../../../common/logo/logo';
import MarketingBar from '../../../common/marketing-bar/marketing-bar';
import WithMobileSize from '../../../common/mobile-size/mobile-size';
import PromoteAppModal from '../../../common/promote-app/promote-app-modal';
import Search from '../../../common/search/search';
import { helpCenter } from '../../../constants/contact-data';
import Routes from '../../../constants/routes';
import { SCHOOL_TYPES } from '../../../constants/school-types';
import useCurrentPathMatch from '../../../hooks/use-current-path-match';
import useIsMobile from '../../../hooks/use-is-mobile';
import { ReactComponent as BurgerIcon } from '../../../images/burger.svg';
import translation, { world } from '../../../languages';
import { AVAILABLE_WORLDS, WORLD_GROUPS } from '../../../languages/constants';
import { getSchools, initNavigationAbTest } from '../../../reducers/school-classification';
import AiToolsNavbar from '../../../routes/ai-tools/components/ai-navbar/ai-tools-navbar';
import { AI_TABS_ITEMS } from '../../../routes/ai-tools/constants/constants';
import useNewInteractive from '../../../routes/new-interactive/hooks/use-new-interactive';
import analytics from '../../../services/analytics';
import cn from '../../../utils/classnames';
import { domainLinkCreator } from '../../../utils/domain-manager';
import hidePopover from '../../../utils/hide-popover';
import isPrerender from '../../../utils/is-prerender';
import isUITest from '../../../utils/is-ui-test';
import PersonalizationUtil from '../../../utils/personalization';
import renderPermissions from '../../../utils/render-permissions';
import session from '../../../utils/session';
import makeUrl from '../../../utils/url/make-url';
import serializeQueryParams from '../../../utils/url/serialize-query-params';
import Extra from './components/extra/extra';
import FavoritesBtn from './components/favorites-btn/favorites-btn';
import FeatureTourStep from './components/feature-tour-step/feature-tour-step';
import InteractiveMenu from './components/interactive-menu/interactive-menu';
import PrerenderMegaMenuLinks from './components/prerender-mega-menu-links/prerender-mega-menu-links';
import SchoolMenu from './components/school-menu/school-menu';
import SchoolsBurger from './components/schools-burger/schools-burger';
import UploadBtn from './components/upload-btn/upload-btn';
import UserMenu from './components/user-menu/user-menu';
import WarenkorbMenu from './components/warenkorb-menu/warenkorb-menu';
import './header.scss';

const BurgerMenu = lazy(() => import('./components/burger-menu/burger-menu'));
const DoubleOptIn = lazy(() => import('../../../common/double-opt-in/double-opt-in'));
const AgbAcceptModal = lazy(() => import('./components/agb-accept-modal/agb-accept-modal'));
const Popover = lazy(() => import('../../../common/popover/popover'));

const RenderSchoolMenu = ({ type }: { type: SCHOOL_TYPES }) => <SchoolMenu type={type} />;
const RenderBurgerMenu = ({ myMaterialsLink }: { myMaterialsLink: string }) => (
    <BurgerMenu materialsLink={myMaterialsLink} />
);
const RenderPopoverLink = ({ title }: { title: string }) => (
    <a className='header-nav-link' href={domainLinkCreator(Routes.HOME.path)}>
        {title}
        <span className='caret' />
    </a>
);

const BurgerPopoverTitle = () => (
    <div className='menu-item' onClick={hidePopover}>
        <BurgerIcon className='burger-icon' />
    </div>
);

const RenderBurger = () => <BurgerIcon className='burger-icon' />;

type HeaderProps = TranslationProps & {
    headerClass: string;
    mobile: boolean;
};

const worldsForExtraNavBar = [AVAILABLE_WORLDS.DE, ...WORLD_GROUPS.SUPPLEMENTARY];

const Header: FC<HeaderProps> = ({ mobile: mobileVisibleSearch, headerClass, t }) => {
    const permissions = renderPermissions();
    const user = useSelector((state: RootState) => state.main.user);
    const isActiveNavigationAbTest = useSelector(
        (state: RootState) => state.main.schoolClassification.isActiveNavigationAbTest
    );
    const dispatch = useDispatch();

    const mobile = useIsMobile(767);
    const isNewInteractive = useNewInteractive();

    const isTesting = isPrerender() || isUITest();
    const isNewFeatureTourParam = !mobile || Boolean(serializeQueryParams().newFeatureTour);
    const isNewFeatureTourStep = !isTesting && isActiveNavigationAbTest && isNewFeatureTourParam;
    const isToolsPage = useCurrentPathMatch(AI_TABS_ITEMS.map((item) => item.path));

    useEffect(() => {
        if (world === AVAILABLE_WORLDS.DE) {
            dispatch(getSchools());
            dispatch(initNavigationAbTest());
        }
    }, []);

    const onSchoolClick = () => {
        analytics.push({
            event: 'gtmEvent',
            eventAction: 'NewSchoolLinkClicked',
            eventCategory: 'mkt-userInteraction',
            eventNonInteraction: false
        });
    };

    const myMaterialsLink =
        user.profileDetails && user.profileDetails.details && user.profileDetails.details.totalMaterials
            ? Routes.CABINET_SALES.path
            : Routes.SELLER.path;

    return (
        <>
            {!mobile && permissions.marketingBar && !isPrerender() && (
                <CountrySpecific allowed={AVAILABLE_WORLDS.DE}>
                    <LiveSessionBar />
                </CountrySpecific>
            )}
            <FreshUserCredits />
            {isNewFeatureTourStep && <FeatureTourStep />}

            {((!mobile && !isActiveNavigationAbTest && world !== AVAILABLE_WORLDS.DE) ||
                (!mobileVisibleSearch && isActiveNavigationAbTest)) && (
                <CountrySpecific allowed={worldsForExtraNavBar}>
                    <Extra myMaterialsLink={myMaterialsLink} />
                </CountrySpecific>
            )}

            {/*<CountrySpecific allowed={[AVAILABLE_WORLDS.DE, AVAILABLE_WORLDS.ES, AVAILABLE_WORLDS.FR]}>*/}
            {/*    <FoldersFeatureAnnouncementPopup />*/}
            {/*</CountrySpecific>*/}
            <div
                className={cn('header main-header flex-column bigger-header', {
                    'd-flex': !headerClass,
                    'simplified-navigation': isActiveNavigationAbTest,
                    'ai-tools-nav': isToolsPage,
                    [headerClass]: headerClass,
                    'highlighted-header': user.loggedInAsAnotherUser,
                    'mobile-visible-search': mobileVisibleSearch,
                    'supplementary-group': !WORLD_GROUPS.MAIN.includes(world),
                    'stiftung-header': permissions.stiftungHeader,
                    'new-interactive': isNewInteractive
                })}
            >
                {mobile && permissions.marketingBar && !isPrerender() && (
                    <CountrySpecific allowed={AVAILABLE_WORLDS.DE}>
                        <LiveSessionBar />
                    </CountrySpecific>
                )}
                {Boolean(
                    session.isLoggedIn() && !user.doubleOptIn && window.location.pathname.indexOf(Routes.AGB.path) < 0
                ) && (
                    <Suspense fallback={<div />}>
                        <DoubleOptIn />
                    </Suspense>
                )}
                {Boolean(
                    session.isLoggedIn() &&
                        user.doubleOptIn &&
                        !user.isAgbAccepted &&
                        window.location.pathname.indexOf(Routes.AGB.path) < 0
                ) && (
                    <Suspense fallback={<div />}>
                        <AgbAcceptModal />
                    </Suspense>
                )}
                <div className='header-wrapper'>
                    <div className='top-section d-flex align-items-center'>
                        <Logo className='logo left d-flex align-items-center justify-content-center' />
                        {!permissions.stiftungHeader && !mobileVisibleSearch && !isNewInteractive && (
                            <div className='d-xs-block d-lg-none'>
                                <Suspense fallback={<div />}>
                                    <Popover
                                        menu={<RenderBurgerMenu myMaterialsLink={myMaterialsLink} />}
                                        mobileTitle={<BurgerPopoverTitle />}
                                        triggerAs={<RenderBurger />}
                                    />
                                </Suspense>
                            </div>
                        )}
                        {!mobileVisibleSearch && <Search />}
                        {!mobileVisibleSearch && isActiveNavigationAbTest && <SchoolsBurger />}
                        <div
                            className={cn('icons-wrapper', {
                                'four-columns': isActiveNavigationAbTest
                            })}
                        >
                            <CountrySpecific allowed={WORLD_GROUPS.MAIN}>
                                <InteractiveMenu />
                            </CountrySpecific>
                            <UserMenu />
                            {!isActiveNavigationAbTest && <UploadBtn link={myMaterialsLink} />}
                            <FavoritesBtn />
                            <WarenkorbMenu />
                        </div>
                    </div>
                    {mobileVisibleSearch && (
                        <div className='d-flex ab-block align-items-center'>
                            {!isNewInteractive && (
                                <div className='d-xs-block d-lg-none'>
                                    <Popover
                                        menu={<RenderBurgerMenu myMaterialsLink={myMaterialsLink} />}
                                        mobileTitle={<BurgerPopoverTitle />}
                                        triggerAs={<RenderBurger />}
                                    />
                                </div>
                            )}
                            <Search mobileVisibleSearch />
                        </div>
                    )}
                    {isToolsPage && (
                        <CountrySpecific allowed={WORLD_GROUPS.MAIN}>
                            <AiToolsNavbar />
                        </CountrySpecific>
                    )}
                    {permissions.menu && !isActiveNavigationAbTest && (
                        <CountrySpecific allowed={WORLD_GROUPS.MAIN}>
                            <nav className='bottom-section align-items-center d-none d-lg-flex'>
                                <PrerenderMegaMenuLinks />
                                <CountrySpecific
                                    allowed={[AVAILABLE_WORLDS.DE, AVAILABLE_WORLDS.ES, AVAILABLE_WORLDS.FR]}
                                >
                                    <>
                                        <Popover
                                            menu={<RenderSchoolMenu type={SCHOOL_TYPES.PRIMARY_SCHOOL} />}
                                            triggerAs={
                                                <RenderPopoverLink title={t('header.navigation.groundschool')} />
                                            }
                                            trigger='click'
                                        />
                                        <Popover
                                            menu={<RenderSchoolMenu type={SCHOOL_TYPES.SECONDARY_SCHOOL} />}
                                            triggerAs={<RenderPopoverLink title={t('header.navigation.secondary')} />}
                                            trigger='click'
                                        />
                                    </>
                                </CountrySpecific>
                                <CustomLink
                                    activeClassName='active'
                                    className={cn('header-nav-link sale-link', {
                                        'first-link': world === AVAILABLE_WORLDS.IT || world === AVAILABLE_WORLDS.GR
                                    })}
                                    to={domainLinkCreator(Routes.SALE_LANDING.path)}
                                >
                                    {t('header.navigation.sale')}
                                </CustomLink>
                                <CustomLink
                                    activeClassName='active'
                                    className='header-nav-link bestseller-link'
                                    // @ts-ignore
                                    isActive={(match, location) => {
                                        const search = serializeQueryParams();

                                        return (
                                            location.pathname.indexOf(Routes.SEARCH_RESULT.path) >= 0 &&
                                            !search[SEARCH_KEYS.FORMAT]?.includes(
                                                FORMAT_FILTER_IDS.INTERACTIVE_STANDALONE.toString()
                                            ) &&
                                            !search[SEARCH_KEYS.FORMAT]?.includes(
                                                FORMAT_FILTER_IDS.INTERACTIVE_HYBRID.toString()
                                            )
                                        );
                                    }}
                                    to={domainLinkCreator(`${Routes.SEARCH_RESULT.path}?query=`)}
                                >
                                    {t('header.navigation.bestseller')}
                                </CustomLink>
                                {/*{ Schedule.summer_end_sale && <span className="summer-end-sale-voucher">27% Rabatt* mit dem Code: <span className="code">COOL19</span></span> }*/}
                                {PersonalizationUtil.showReco() && (
                                    <CustomLink
                                        activeClassName='active'
                                        className='header-nav-link empfehlungen'
                                        to={domainLinkCreator(Routes.SEASONAL_MATERIALS_LANDING.path)}
                                    >
                                        {t('header.navigation.recommendations')}
                                    </CustomLink>
                                )}
                                <CountrySpecific allowed={[AVAILABLE_WORLDS.DE]}>
                                    <CustomLink
                                        activeClassName='active'
                                        className='header-nav-link podcast'
                                        to={domainLinkCreator(
                                            makeUrl(
                                                Routes.AUTHOR_MAIN.path,
                                                { id: 1236, slug: 'eduki-d-a-ch' },
                                                { tab: 'Blog' }
                                            )
                                        )}
                                    >
                                        {t('header.navigation.magazine')}
                                    </CustomLink>
                                </CountrySpecific>
                                <CountrySpecific
                                    allowed={[AVAILABLE_WORLDS.FR, AVAILABLE_WORLDS.IT, AVAILABLE_WORLDS.GR]}
                                >
                                    <CustomLink
                                        activeClassName='active'
                                        className={cn('my-materials-link header-nav-link yellow-text', {
                                            'incentive-link': world === AVAILABLE_WORLDS.DE,
                                            'shifted-to-bottom':
                                                world === AVAILABLE_WORLDS.ES || world === AVAILABLE_WORLDS.FR
                                        })}
                                        to={domainLinkCreator(myMaterialsLink)}
                                    >
                                        <b>{t('header.navigation.shareMaterial')}</b>
                                    </CustomLink>
                                </CountrySpecific>
                                <a
                                    className={`header-nav-link ${
                                        world === AVAILABLE_WORLDS.ES && 'hide-on-small-screen'
                                    }`}
                                    href={domainLinkCreator(helpCenter[world])}
                                >
                                    {t('header.navigation.help')}
                                </a>
                                <CountrySpecific allowed={[AVAILABLE_WORLDS.FR]}>
                                    <CustomLink
                                        exact
                                        className={cn('header-nav-link')}
                                        to={domainLinkCreator(Routes.RUSSIA_ATTACKS_UKRAINE_LANDING.path)}
                                    >
                                        #StandWithUkraine
                                    </CustomLink>
                                </CountrySpecific>
                                <CountrySpecific allowed={[AVAILABLE_WORLDS.DE]}>
                                    <CustomLink
                                        exact
                                        onClick={onSchoolClick}
                                        className={cn('header-nav-link school-account new-gradient')}
                                        to={domainLinkCreator(Routes.SCHOOL_ACCOUNT.path)}
                                    >
                                        <div className={cn('school-link-text label-caption')}>Schulaccount</div>
                                    </CustomLink>
                                </CountrySpecific>
                            </nav>
                        </CountrySpecific>
                    )}
                </div>
                <>{mobile && permissions.marketingBar && !isPrerender() && <MarketingBar />}</>
                {window.innerWidth < 991 && !isUITest() && <PromoteAppModal />}
            </div>
            <>{!mobile && permissions.marketingBar && !isPrerender() && <MarketingBar />}</>
        </>
    );
};

const translate = translation('header');

//Used breakpoint 992 for avoiding UI crash on 991 screen resolution
export default withRouter(WithMobileSize(translate(memo(Header)), 992));
